import { useState, useEffect, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { Button } from "./ui/button";
import { SITE_LOGO } from "../data/content";

const REVIEWS = [
  { label: "Travel Club Reviews", to: "/travel-club-reviews" },
  { label: "Timeshare Reviews", to: "/timeshare-reviews" },
  { label: "Presentation Guide", to: "/timeshare-reviews/presentation-guide" },
];
const NAV = [
  { label: "New Members", to: "/members" },
  { label: "Guides", to: "/guides" },
  { label: "Travel Blog", to: "/blog" },
  { label: "Packages", to: "/packages" },
  { label: "Contact", to: "/contact" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [reviewsOpen, setReviewsOpen] = useState(false);
  const location = useLocation();
  const closeTimer = useRef(null);

  useEffect(() => {
    setOpen(false);
    setReviewsOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const openMenu = () => {
    clearTimeout(closeTimer.current);
    setReviewsOpen(true);
  };
  const closeMenu = () => {
    closeTimer.current = setTimeout(() => setReviewsOpen(false), 120);
  };

  const linkClass = ({ isActive }) =>
    `rounded-full px-4 py-2 text-sm font-semibold transition-colors duration-200 ${
      isActive ? "bg-navy text-white" : "text-slate-600 hover:bg-slate-100 hover:text-navy"
    }`;

  const reviewsActive = location.pathname.includes("review");

  return (
    <header
      data-testid="site-navbar"
      className={`sticky top-0 z-50 transition-shadow duration-300 glass-nav border-b ${
        scrolled ? "border-slate-200 shadow-[0_8px_30px_rgba(15,23,42,0.06)]" : "border-transparent"
      }`}
    >
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 lg:px-8">
        <Link to="/" data-testid="nav-logo-link" className="group flex items-center gap-2.5">
          <img
            src={SITE_LOGO}
            alt="Travel Club Navigator Group logo — globe with airplane and arrow"
            className="h-11 w-11 object-contain transition-transform duration-300 group-hover:-rotate-6"
          />
          <span className="flex flex-col leading-none">
            <span className="font-display text-lg font-extrabold tracking-tight text-navy">
              Navigator
            </span>
            <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-secondary">
              Travel Club Group
            </span>
          </span>
        </Link>

        <div className="hidden items-center gap-1 lg:flex">
          <div className="relative" onMouseEnter={openMenu} onMouseLeave={closeMenu}>
            <button
              data-testid="nav-reviews-dropdown"
              onClick={() => setReviewsOpen((v) => !v)}
              className={`flex items-center gap-1 rounded-full px-4 py-2 text-sm font-semibold transition-colors duration-200 ${
                reviewsActive ? "bg-navy text-white" : "text-slate-600 hover:bg-slate-100 hover:text-navy"
              }`}
            >
              Reviews
              <ChevronDown className={`h-4 w-4 transition-transform ${reviewsOpen ? "rotate-180" : ""}`} />
            </button>
            {reviewsOpen && (
              <div className="absolute left-0 top-full w-60 pt-2">
                <div className="overflow-hidden rounded-2xl border border-slate-100 bg-white p-2 shadow-xl">
                  {REVIEWS.map((r) => (
                    <NavLink
                      key={r.to}
                      to={r.to}
                      data-testid={`nav-link-${r.to.replace("/", "")}`}
                      className="block rounded-xl px-4 py-2.5 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-100 hover:text-navy"
                    >
                      {r.label}
                    </NavLink>
                  ))}
                </div>
              </div>
            )}
          </div>

          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-link-${item.to.replace("/", "")}`}
              className={linkClass}
            >
              {item.label}
            </NavLink>
          ))}
        </div>

        <div className="hidden items-center gap-2 lg:flex">
          <Link
            to="/partners"
            data-testid="nav-partner-link"
            className="rounded-full px-3 py-2 text-sm font-semibold text-slate-500 transition-colors hover:text-primary"
          >
            Partner With Us
          </Link>
          <Button
            asChild
            data-testid="nav-cta-button"
            className="rounded-full bg-primary px-6 font-semibold text-primary-foreground btn-glow transition-transform duration-200 hover:scale-105 hover:bg-primary/90"
          >
            <Link to="/packages">Explore Packages</Link>
          </Button>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-navy lg:hidden"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </nav>

      {open && (
        <div data-testid="nav-mobile-menu" className="border-t border-slate-200 bg-white px-5 pb-6 pt-2 lg:hidden">
          {[...REVIEWS, ...NAV, { label: "Partner With Us", to: "/partners" }].map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `block rounded-xl px-4 py-3 text-base font-semibold ${
                  isActive ? "bg-navy text-white" : "text-slate-700 hover:bg-slate-100"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
          <Button asChild className="mt-3 w-full rounded-full bg-primary font-semibold text-primary-foreground">
            <Link to="/packages">Explore Packages</Link>
          </Button>
        </div>
      )}
    </header>
  );
};
